import { Fragment, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { qs, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { fmt } from "@/lib/utils-msip";
import { DPC_STAGES, pctTone, heatBg, fmtPct, PERIOD_OPTIONS, monthOptions, monthLabel, stageShort, STATUS_META } from "@/lib/dpc";
import { SalesTargetPanel } from "@/components/TargetPanel";
import { ProratedBadge, MTD_NOTE } from "@/components/PlanBits";
import { TdCell } from "@/components/overview/TdFleet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const TH = "px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground";
const TOTAL_ROW = "border-t-2 border-foreground/30 bg-secondary font-bold";

const sumBy = (rows, pick) => rows.reduce((acc, r) => acc + (Number(pick(r)) || 0), 0);
const pctOf = (ach, plan) => (plan > 0 ? Math.round((ach / plan) * 1000) / 10 : null);
const fmtPlan = (p) => (Number.isInteger(p) ? p : Number(p).toFixed(1));

function heatTotals(heat) {
  const cells = {};
  DPC_STAGES.forEach((s) => {
    const plan = sumBy(heat, (h) => h.cells[s]?.plan);
    const ach = sumBy(heat, (h) => h.cells[s]?.ach);
    cells[s] = { plan, ach, pct: pctOf(ach, plan) };
  });
  return cells;
}

export function DpcControls({ month, setMonth, period, setPeriod, extra }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={month} onValueChange={setMonth}>
        <SelectTrigger className="h-8 w-[120px] text-xs" data-testid="dpc-month-select"><SelectValue /></SelectTrigger>
        <SelectContent>{monthOptions().map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
      </Select>
      {setPeriod && (
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="h-8 w-[120px] text-xs" data-testid="dpc-period-select"><SelectValue /></SelectTrigger>
          <SelectContent>{PERIOD_OPTIONS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {extra}
    </div>
  );
}

export function StageTiles({ tiles, note = true }) {
  const order = ["RETAIL", "BOOKING", "TEST DRIVE", "ENQUIRY", "LEAD"];
  return (
    <div>
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
      {order.map((s, i) => {
        const t = tiles[s] || {};
        return (
          <div key={s} className={`border bg-card rounded-md p-4 ${i === 0 ? "ring-1 ring-foreground/30" : ""}`} data-testid={`dpc-tile-${s.replace(/\s/g, "-")}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{s}</span>
              <ProratedBadge count={t.prorated} testId={`dpc-tile-${s.replace(/\s/g, "-")}-prorated`} />
            </div>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="num text-2xl font-bold">{fmt(t.ach)}</span>
              <span className="text-xs text-muted-foreground">/ {t.plan_td != null ? fmt(Math.round(t.plan_td)) : "—"} plan</span>
            </div>
            <div className={`text-sm font-bold num ${pctTone(t.pct)}`}>{fmtPct(t.pct)}</div>
            <div className="text-[11px] text-muted-foreground mt-1">
              Gap {t.gap != null ? fmt(Math.round(t.gap)) : "—"} · Proj {fmt(t.projected)} · Need {t.required_rate != null ? t.required_rate : "—"}/day
            </div>
          </div>
        );
      })}
    </div>
    {note && <p className="text-[10px] text-muted-foreground mt-1.5" data-testid="dpc-mtd-note">{MTD_NOTE}</p>}
    </div>
  );
}

export function DealerPicker({ meta, value, extraLabel }) {
  const navigate = useNavigate();
  return (
    <Select value={value || "ALL"} onValueChange={(v) => navigate(v === "ALL" ? "/dpc" : `/dpc/dealer/${v}`)}>
      <SelectTrigger className="h-8 w-[210px] text-xs" data-testid="dpc-dealer-picker"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="ALL">{extraLabel || "All locations"}</SelectItem>
        {(meta?.dealers || []).map((d) => <SelectItem key={d.code} value={d.code}>{d.code} · {d.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function DpcCommand() {
  const { user } = useApp();
  const [meta, setMeta] = useState(null);
  const [month, setMonth] = useState("");
  const [period, setPeriod] = useState("mtd");
  const [model, setModel] = useState("");
  const [source, setSource] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dpc/meta").then(({ data }) => { setMeta(data); setMonth((m) => m || data.month); }).catch((e) => toast.error(apiError(e)));
  }, []);

  useEffect(() => {
    if (!month) return;
    api.get(`/dpc/dashboard/group?${qs({ month, period, model, source })}`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [month, period, model, source]);

  const scopeLabel = user?.role === "zonal" ? "Zone view" : user?.role === "manager" ? "My dealers" : "Group view";
  const totalLabel = user?.role === "zonal" ? "Zone total" : user?.role === "manager" ? "Total" : "Group total";

  if (!data) return <div className="text-sm text-muted-foreground py-20 text-center">Loading DPC cockpit…</div>;
  const maxFunnel = Math.max(...data.funnel.map((f) => Math.max(f.plan || 0, f.ach || 0)), 1);
  const heatTotal = heatTotals(data.heat);
  const modelTotal = { plan_td: sumBy(data.model_table, (r) => r.plan_td), ach: sumBy(data.model_table, (r) => r.ach), gap: sumBy(data.model_table, (r) => r.gap) };
  const sourceTotal = { plan_td: sumBy(data.source_table, (r) => r.plan_td), ach: sumBy(data.source_table, (r) => r.ach), gap: sumBy(data.source_table, (r) => r.gap) };

  return (
    <div className="space-y-5" data-testid="dpc-command">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight">DPC Cockpit <span className="text-muted-foreground font-medium text-base">· {scopeLabel}</span></h1>
        <DpcControls month={month} setMonth={setMonth} period={period} setPeriod={setPeriod} extra={
          <>
            <DealerPicker meta={meta} value="" extraLabel={user?.role === "zonal" ? "All zone locations" : "All locations"} />            <Select value={model || "ALL"} onValueChange={(v) => setModel(v === "ALL" ? "" : v)}>
              <SelectTrigger className="h-8 w-[120px] text-xs" data-testid="dpc-model-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="ALL">All models</SelectItem>{(meta?.models || []).filter((x) => x.active).map((x) => <SelectItem key={x.name} value={x.name}>{x.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={source || "ALL"} onValueChange={(v) => setSource(v === "ALL" ? "" : v)}>
              <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="dpc-source-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="ALL">All sources</SelectItem>{(meta?.sources || []).filter((x) => x.active).map((x) => <SelectItem key={x.name} value={x.name}>{x.name}</SelectItem>)}</SelectContent>
            </Select>
          </>
        } />
      </div>

      <StageTiles tiles={data.tiles} />

      <SalesTargetPanel month={month} myCodes={user?.dealer_codes || []} showRace />

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="border bg-card rounded-md p-4" data-testid="dpc-funnel">
          <h3 className="font-heading text-sm font-semibold mb-3">Funnel — plan vs achieved</h3>
          <div className="space-y-3">
            {data.funnel.map((f) => (
              <div key={f.stage}>
                <div className="flex justify-between text-[11px] mb-1">
                  <span className="font-semibold">{f.stage}</span>
                  <span className="text-muted-foreground num">{fmt(f.ach)} / {fmt(Math.round(f.plan))}{f.conv_ach != null && <span className="ml-2">conv {f.conv_ach}% (plan {f.conv_plan ?? "—"}%)</span>}</span>
                </div>
                <div className="h-4 bg-secondary rounded-sm relative overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-foreground/15" style={{ width: `${(f.plan / maxFunnel) * 100}%` }} />
                  <div className="absolute inset-y-0 left-0 bg-[#BB162B]" style={{ width: `${(f.ach / maxFunnel) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 border bg-card rounded-md overflow-auto max-h-[420px]" data-testid="dpc-heat-grid">
          <div className="px-4 py-3 border-b sticky top-0 bg-card z-10 flex items-center justify-between">
            <h3 className="font-heading text-sm font-semibold">Dealer heat-grid <span className="text-muted-foreground font-normal text-xs">achievement % · click a dealer</span></h3>
          </div>
          <table className="w-full text-xs">
            <thead className="border-b"><tr><th className={TH}>Dealer</th>{DPC_STAGES.map((s) => <Fragment key={s}><th className={`${TH} text-right`}>{stageShort(s)}</th>{s === "TEST DRIVE" && <th className={`${TH} text-right border-l-2 border-border/80`} title={data.td_definition} data-testid="dpc-heat-td-head">TDs/car/day</th>}</Fragment>)}</tr></thead>
            <tbody>
              {data.heat.map((h) => (
                <tr key={h.dealer_code} className="border-b last:border-0 hover:bg-muted/50" data-testid={`dpc-heat-${h.dealer_code}`}>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <Link to={`/dpc/dealer/${h.dealer_code}`} className="font-medium hover:underline">{h.dealer_code}</Link>
                    <span className="text-muted-foreground ml-1.5">{h.dealer_name}</span>
                    {h.status !== "approved" && <span className={`ml-1.5 rounded px-1 py-0.5 text-[9px] ${STATUS_META[h.status]?.cls}`}>{STATUS_META[h.status]?.label}</span>}
                    <ProratedBadge count={h.prorated ? 1 : 0} className="ml-1.5" testId={`dpc-heat-prorated-${h.dealer_code}`} />
                  </td>
                  {DPC_STAGES.map((s) => {
                    const c = h.cells[s];
                    return <Fragment key={s}><td className={`px-2 py-1.5 text-right num font-semibold ${heatBg(c.pct)}`} title={`${c.ach} / ${fmtPlan(c.plan)}`}>{fmtPct(c.pct)}</td>{s === "TEST DRIVE" && <TdCell f={h.td_fleet} testId={`dpc-heat-${h.dealer_code}-td`} py="py-1.5" />}</Fragment>;
                  })}
                </tr>
              ))}
              {data.heat.length > 0 && (
                <tr className={TOTAL_ROW} data-testid="dpc-heat-total">
                  <td className="sticky bottom-0 bg-secondary border-t-2 border-foreground/30 px-2 py-1.5 whitespace-nowrap uppercase tracking-wider text-[10px]">{totalLabel} <span className="text-muted-foreground font-normal normal-case">· {data.heat.length} dealers · achieved ÷ plan-to-date</span></td>
                  {DPC_STAGES.map((s) => {
                    const c = heatTotal[s];
                    return (
                      <Fragment key={s}>
                      <td className={`sticky bottom-0 border-t-2 border-foreground/30 px-2 py-1 text-right num ${c.pct == null ? "bg-secondary" : heatBg(c.pct)}`} data-testid={`dpc-heat-total-${s.replace(/\s/g, "-")}`}>
                        <div>{fmtPct(c.pct)}</div>
                        <div className="text-[9px] font-normal text-muted-foreground" data-testid={`dpc-heat-total-frac-${s.replace(/\s/g, "-")}`}>{c.ach} / {fmtPlan(c.plan)}</div>
                      </td>
                      {s === "TEST DRIVE" && <TdCell f={data.td_fleet_total} testId="dpc-heat-total-td" bg="sticky bottom-0 bg-secondary border-t-2 border-foreground/30" py="py-1" agg />}
                      </Fragment>
                    );
                  })}
                </tr>
              )}
            </tbody>
          </table>
          {data.td_fleet_unset?.length > 0 && <p className="px-4 py-2 text-[10px] text-amber-800 border-t" data-testid="dpc-heat-td-unset">TD cars not set: {data.td_fleet_unset.join(", ")} — enter them in Admin › Dealers.</p>}
        </div>
      </div>

      <div className="border bg-card rounded-md p-4" data-testid="dpc-today-strip">
        <h3 className="font-heading text-sm font-semibold mb-2">Latest day — {data.today_strip.date || "no data yet"}</h3>
        {data.today_strip.rows.length === 0 && <p className="text-xs text-muted-foreground">No achievement rows uploaded yet.</p>}
        <div className="flex flex-wrap gap-1.5">
          {data.today_strip.rows.map((r) => (
            <Link key={r.dealer_code} to={`/dpc/dealer/${r.dealer_code}`} className="rounded border bg-secondary/60 px-2 py-1 text-xs hover:bg-secondary transition-colors duration-200">
              {r.dealer_code} · R <b className="num">{r.retail}</b> B <b className="num">{r.booking}</b> TD <b className="num">{r.test_drive}</b> E <b className="num">{r.enquiry}</b>
            </Link>
          ))}
        </div>
        {data.today_strip.missing.length > 0 && (
          <p className="text-xs text-red-600 mt-2" data-testid="dpc-missing-dealers">Missing upload ({data.today_strip.missing.length}): {data.today_strip.missing.join(", ")}</p>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="border bg-card rounded-md overflow-auto" data-testid="dpc-model-table">
          <div className="px-4 py-3 border-b"><h3 className="font-heading text-sm font-semibold">Model performance — Retail</h3></div>
          <table className="w-full text-xs">
            <thead className="border-b"><tr>{["Model", "Plan-td", "Ach", "%", "Gap", "Mix ach", "Mix plan"].map((h, i) => <th key={h} className={`${TH} ${i > 0 ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
            <tbody>
              {data.model_table.map((r) => (
                <tr key={r.model} className="border-b last:border-0 hover:bg-muted/50">
                  <td className="px-2 py-1.5 font-medium">{r.model}</td>
                  <td className="px-2 py-1.5 text-right num">{Math.round(r.plan_td)}</td>
                  <td className="px-2 py-1.5 text-right num font-bold">{r.ach}</td>
                  <td className={`px-2 py-1.5 text-right num font-semibold ${pctTone(r.pct)}`}>{fmtPct(r.pct)}</td>
                  <td className="px-2 py-1.5 text-right num">{Math.round(r.gap)}</td>
                  <td className="px-2 py-1.5 text-right num text-muted-foreground">{fmtPct(r.mix_ach)}</td>
                  <td className="px-2 py-1.5 text-right num text-muted-foreground">{fmtPct(r.mix_plan)}</td>
                </tr>
              ))}
              {data.model_table.length > 0 && (
                <tr className={TOTAL_ROW} data-testid="dpc-model-total">
                  <td className="px-2 py-1.5 uppercase tracking-wider text-[10px]">Total</td>
                  <td className="px-2 py-1.5 text-right num">{Math.round(modelTotal.plan_td)}</td>
                  <td className="px-2 py-1.5 text-right num" data-testid="dpc-model-total-ach">{modelTotal.ach}</td>
                  <td className={`px-2 py-1.5 text-right num ${pctTone(pctOf(modelTotal.ach, modelTotal.plan_td))}`}>{fmtPct(pctOf(modelTotal.ach, modelTotal.plan_td))}</td>
                  <td className="px-2 py-1.5 text-right num">{Math.round(modelTotal.gap)}</td>
                  <td className="px-2 py-1.5 text-right num text-muted-foreground">{modelTotal.ach > 0 ? "100%" : "—"}</td>
                  <td className="px-2 py-1.5 text-right num text-muted-foreground">{modelTotal.plan_td > 0 ? "100%" : "—"}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="border bg-card rounded-md overflow-auto" data-testid="dpc-source-table">
          <div className="px-4 py-3 border-b"><h3 className="font-heading text-sm font-semibold">Source performance — Enquiries</h3></div>
          <table className="w-full text-xs">
            <thead className="border-b"><tr>{["Source", "Plan-td", "Ach", "%", "Gap", "Mix ach"].map((h, i) => <th key={h} className={`${TH} ${i > 0 ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
            <tbody>
              {data.source_table.map((r) => (
                <tr key={r.source} className="border-b last:border-0 hover:bg-muted/50">
                  <td className="px-2 py-1.5 font-medium" title={(meta?.sources || []).find((x) => x.name === r.source)?.members?.join(", ")}>{r.source}</td>
                  <td className="px-2 py-1.5 text-right num">{Math.round(r.plan_td)}</td>
                  <td className="px-2 py-1.5 text-right num font-bold">{r.ach}</td>
                  <td className={`px-2 py-1.5 text-right num font-semibold ${pctTone(r.pct)}`}>{fmtPct(r.pct)}</td>
                  <td className="px-2 py-1.5 text-right num">{r.gap != null ? Math.round(r.gap) : "—"}</td>
                  <td className="px-2 py-1.5 text-right num text-muted-foreground">{fmtPct(r.mix_ach)}</td>
                </tr>
              ))}
              {data.source_table.length > 0 && (
                <tr className={TOTAL_ROW} data-testid="dpc-source-total">
                  <td className="px-2 py-1.5 uppercase tracking-wider text-[10px]">Total</td>
                  <td className="px-2 py-1.5 text-right num">{Math.round(sourceTotal.plan_td)}</td>
                  <td className="px-2 py-1.5 text-right num" data-testid="dpc-source-total-ach">{sourceTotal.ach}</td>
                  <td className={`px-2 py-1.5 text-right num ${pctTone(pctOf(sourceTotal.ach, sourceTotal.plan_td))}`}>{fmtPct(pctOf(sourceTotal.ach, sourceTotal.plan_td))}</td>
                  <td className="px-2 py-1.5 text-right num">{Math.round(sourceTotal.gap)}</td>
                  <td className="px-2 py-1.5 text-right num text-muted-foreground">{sourceTotal.ach > 0 ? "100%" : "—"}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
